import moment from "moment";
import React from "react";
import { useSelector } from "react-redux";

import { AppState } from "shared/data/reducers";
import { dayMonthFormat, hourMinuteFormat } from "shared/lib/datetime";
import { capitalizeOnlyFirstLetter } from "shared/lib/functions";
import { FlightSegment } from "shared/lib/golObjectTypes/SearchFlightResponse/Response";

import Img from "@components/FileServer/Img";
import FormattedMessage from "@components/UI/FormattedMessage";

import AirlineTitle from "../Formatters/AirlineTitle";
import AirportTitle from "../Formatters/AirportTitle";
import FormatDuration from "../Formatters/FormatDuration";

interface Props {
  underTransfer: boolean;
  oFlightSegment: FlightSegment;
}

const FlightDetailSegment: React.FC<Props> = (props) => {
  const oTransportCompanies = useSelector(
    (state: AppState) => state.search.oTransportCompanies
  );
  const { defaultCountry, lang, oAirplanes } = useSelector(
    (state: AppState) => ({
      lang: state.requestorConfig.currentLanguage,
      defaultCountry:
        state.storage.frontendSettings?.dealerFrontendSettings
          ?.defaultCountry ?? "",
      oAirplanes: state.search.airplanes,
    })
  );

  const { oFlightSegment } = props;

  const hasTechnicalStops =
    oFlightSegment &&
    (oFlightSegment as any).TechnicalStops !== undefined &&
    (oFlightSegment as any).TechnicalStops?.TechnicalStop?.length > 0;

  return (
    <div className="flight-detail-one-way-flight">
      <Img
        src="/static/images/ico-plane.svg"
        className="flight-detail-one-way-flight-icon"
        alt="plane icon"
      />
      <div
        className={`flight-detail-one-way-flight-wrapper ${
          props.underTransfer
            ? " flight-detail-one-way-flight-wrapper__padding-top"
            : ""
        }`}
      >
        <div className="flight-detail-one-way-flight-flight">
          <Img
            src="/static/images/ico-dot.svg"
            className="flight-detail-one-way-flight-dot"
            alt="dot icon"
          />
          <div className="flight-detail-one-way-flight-flight-text-image-wrapper">
            <div className="flight-detail-one-way-flight-flight-row">
              <div className="flight-detail-one-way-flight-flight-row-times">
                <b>
                  {moment(oFlightSegment.DepartureDateTime).format(
                    hourMinuteFormat(defaultCountry)
                  )}
                </b>{" "}
                |{" "}
                {moment(oFlightSegment.DepartureDateTime).format(
                  dayMonthFormat(defaultCountry)
                )}{" "}
                {capitalizeOnlyFirstLetter(
                  moment(new Date(oFlightSegment.DepartureDateTime))
                    .locale(lang)
                    .format("dddd")
                    .toUpperCase()
                )}{" "}
              </div>
              <div className="flight-detail-one-way-flight-flight-row-destination">
                <AirportTitle airportCode={oFlightSegment.OriginAirport} />
              </div>
            </div>
            <img
              src={
                oTransportCompanies[oFlightSegment.MarketingAirline] !==
                undefined
                  ? oTransportCompanies[oFlightSegment.MarketingAirline].LogoUrl
                      .$t
                  : null
              }
              className="flight-detail-one-way-flight-flight-text-image"
              alt="Airline logo"
            />
          </div>
        </div>
        <div className="flight-detail-one-way-flight-flight">
          <Img
            src="/static/images/ico-dot.svg"
            className="flight-detail-one-way-flight-dot"
            alt="dot icon"
          />
          <div className="flight-detail-one-way-flight-flight-text-image-wrapper">
            <div className="flight-detail-one-way-flight-flight-row">
              <div className="flight-detail-one-way-flight-flight-row-times">
                <b>
                  {moment(oFlightSegment.ArrivalDateTime).format(
                    hourMinuteFormat(defaultCountry)
                  )}
                </b>{" "}
                |{" "}
                {moment(oFlightSegment.ArrivalDateTime).format(
                  dayMonthFormat(defaultCountry)
                )}{" "}
                {capitalizeOnlyFirstLetter(
                  moment(new Date(oFlightSegment.ArrivalDateTime))
                    .locale(lang)
                    .format("dddd")
                    .toUpperCase()
                )}{" "}
              </div>
              <div className="flight-detail-one-way-flight-flight-row-destination">
                <AirportTitle airportCode={oFlightSegment.DestinationAirport} />
              </div>
            </div>
          </div>
        </div>
        <div>
          <FormattedMessage id="Flight.flightDuration" />:{" "}
          <FormatDuration
            showNothingIfNotDefined
            duration={oFlightSegment.JourneyDuration}
          />
        </div>
        <div>
          <FormattedMessage id="Flight.airline" />
          :&nbsp;
          <b>
            <AirlineTitle airlineCode={oFlightSegment.MarketingAirline} />
          </b>
        </div>
        <div>
          <FormattedMessage id="Flight.flightNumber" />
          :&nbsp;
          <b>
            {oFlightSegment.MarketingAirline}
            {oFlightSegment.FlightNumber}
          </b>{" "}
          <FormattedMessage id="Flight.flightClass" />
          :&nbsp;
          {oFlightSegment.AvailableBookingClasses.AvailableBookingClass[0].Code}
          {oFlightSegment.OperatingAirline !==
            oFlightSegment.MarketingAirline && (
            <div>
              <FormattedMessage id="FlightDetailSegment.operating" />:{" "}
              <b>
                <AirlineTitle airlineCode={oFlightSegment.OperatingAirline} />
              </b>
            </div>
          )}
          {oAirplanes && oFlightSegment.PlaneType && (
            <div>
              <FormattedMessage id="FlightDetailSegment.planeType" />
              :&nbsp;{oAirplanes[oFlightSegment.PlaneType]}
            </div>
          )}
        </div>
        {hasTechnicalStops && (
          <div className="flight-detail-segment-technical-stop">
            <FormattedMessage id="FlightDetailSegment.technicalStopAttention" />
            :{" "}
            <AirportTitle
              airportCode={
                (oFlightSegment as any).TechnicalStops.TechnicalStop[0]
                  .Destination
              }
            />
            . <FormattedMessage id="FlightDetailSegment.technicalStopVisa" />
          </div>
        )}
        <br />
      </div>
    </div>
  );
};

export default FlightDetailSegment;
